import React, { useState, useEffect } from 'react';
import {
    FaUniversalAccess,
    FaFont,
    FaAdjust,
    FaLink,
    FaMousePointer,
    FaImage,
    FaUndo,
    FaTimes,
    FaSearchPlus,
    FaSearchMinus,
    FaTextHeight,
    FaEye,
    FaKeyboard,
    FaHandPaper
} from 'react-icons/fa';

const AccessibilityMenu = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [settings, setSettings] = useState({
        fontSize: 100, // percentage
        contrast: 'normal', // normal, high, dark
        highlightLinks: false,
        largeCursor: false,
        hideImages: false,
        readableFont: false,
        letterSpacing: 'normal',
        lineHeight: 'normal'
    });

    // Load saved settings
    useEffect(() => {
        const savedSettings = localStorage.getItem('accessibilitySettings');
        if (savedSettings) {
            try {
                const parsed = JSON.parse(savedSettings);
                setSettings(parsed);
                applySettings(parsed);
            } catch (e) {
                console.error('Error loading accessibility settings');
            }
        }
    }, []);

    // Apply settings to document
    const applySettings = (newSettings) => {
        const root = document.documentElement;

        // Font Size
        root.style.fontSize = `${newSettings.fontSize}%`;

        // Contrast
        root.classList.remove('high-contrast', 'dark-contrast', 'inverted-colors');
        if (newSettings.contrast === 'high') {
            root.classList.add('high-contrast');
        } else if (newSettings.contrast === 'dark') {
            root.classList.add('dark-contrast');
        }

        // Highlight Links
        if (newSettings.highlightLinks) {
            root.classList.add('highlight-links');
        } else {
            root.classList.remove('highlight-links');
        }

        // Large Cursor
        if (newSettings.largeCursor) {
            root.classList.add('large-cursor');
        } else {
            root.classList.remove('large-cursor');
        }

        // Hide Images
        if (newSettings.hideImages) {
            root.classList.add('hide-images');
        } else {
            root.classList.remove('hide-images');
        }

        // Readable Font
        if (newSettings.readableFont) {
            root.classList.add('readable-font');
        } else {
            root.classList.remove('readable-font');
        }

        // Letter Spacing
        if (newSettings.letterSpacing === 'wide') {
            root.classList.add('wide-letter-spacing');
        } else {
            root.classList.remove('wide-letter-spacing');
        }

        // Line Height
        if (newSettings.lineHeight === 'tall') {
            root.classList.add('tall-line-height');
        } else {
            root.classList.remove('tall-line-height');
        }
    };

    const updateSetting = (key, value) => {
        const newSettings = { ...settings, [key]: value };
        setSettings(newSettings);
        applySettings(newSettings);
        localStorage.setItem('accessibilitySettings', JSON.stringify(newSettings));
    };

    const resetSettings = () => {
        const defaultSettings = {
            fontSize: 100,
            contrast: 'normal',
            highlightLinks: false,
            largeCursor: false,
            hideImages: false,
            readableFont: false,
            letterSpacing: 'normal',
            lineHeight: 'normal'
        };
        setSettings(defaultSettings);
        applySettings(defaultSettings);
        localStorage.removeItem('accessibilitySettings');
    };

    const toggleSetting = (key) => {
        updateSetting(key, !settings[key]);
    };

    return (
        <>
            {/* Trigger Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="fixed top-4 right-4 z-[9999] bg-gradient-to-r from-[#1E3A8A] to-[#0D9488] text-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 group"
                aria-label="Menu Aksesibilitas"
                title="Menu Aksesibilitas untuk Disabilitas"
            >
                <FaUniversalAccess className="text-2xl group-hover:animate-pulse" />
                <span className="absolute -bottom-8 right-0 bg-[#0F172A] text-white text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                    Aksesibilitas
                </span>
            </button>

            {/* Accessibility Panel */}
            {isOpen && (
                <div className="fixed top-20 right-4 z-[9998] w-80 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-[#1E3A8A] to-[#0D9488] text-white p-4 flex items-center justify-between">
                        <div className="flex items-center">
                            <FaUniversalAccess className="text-2xl mr-3" />
                            <div>
                                <h3 className="font-bold">Menu Aksesibilitas</h3>
                                <p className="text-xs opacity-80">Untuk penyandang disabilitas</p>
                            </div>
                        </div>
                        <button
                            onClick={() => setIsOpen(false)}
                            className="text-white/80 hover:text-white transition-colors"
                        >
                            <FaTimes />
                        </button>
                    </div>

                    {/* Settings */}
                    <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
                        {/* Font Size */}
                        <div className="bg-gray-50 rounded-lg p-3">
                            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                                <FaSearchPlus className="mr-2 text-[#0D9488]" />
                                Ukuran Font
                            </label>
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={() => {
                                        const newSize = Math.max(70, settings.fontSize - 10);
                                        updateSetting('fontSize', newSize);
                                    }}
                                    className="p-2 bg-white border rounded-lg hover:bg-gray-100"
                                    disabled={settings.fontSize <= 70}
                                >
                                    <FaSearchMinus className="text-gray-600" />
                                </button>
                                <div className="flex-1 bg-gray-200 rounded-full h-2">
                                    <div
                                        className="bg-[#0D9488] h-2 rounded-full transition-all"
                                        style={{ width: `${((settings.fontSize - 70) / 130) * 100}%` }}
                                    />
                                </div>
                                <button
                                    onClick={() => {
                                        const newSize = Math.min(200, settings.fontSize + 10);
                                        updateSetting('fontSize', newSize);
                                    }}
                                    className="p-2 bg-white border rounded-lg hover:bg-gray-100"
                                    disabled={settings.fontSize >= 200}
                                >
                                    <FaSearchPlus className="text-gray-600" />
                                </button>
                                <span className="text-sm font-medium w-12 text-center">
                                    {settings.fontSize}%
                                </span>
                            </div>
                        </div>

                        {/* Contrast */}
                        <div className="bg-gray-50 rounded-lg p-3">
                            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                                <FaAdjust className="mr-2 text-[#0D9488]" />
                                Kontras Warna
                            </label>
                            <div className="grid grid-cols-3 gap-2">
                                <button
                                    onClick={() => updateSetting('contrast', 'normal')}
                                    className={`p-2 rounded-lg text-sm font-medium transition-all ${settings.contrast === 'normal'
                                            ? 'bg-[#0D9488] text-white'
                                            : 'bg-white border hover:bg-gray-50'
                                        }`}
                                >
                                    Normal
                                </button>
                                <button
                                    onClick={() => updateSetting('contrast', 'high')}
                                    className={`p-2 rounded-lg text-sm font-medium transition-all ${settings.contrast === 'high'
                                            ? 'bg-[#0D9488] text-white'
                                            : 'bg-white border hover:bg-gray-50'
                                        }`}
                                >
                                    Kontras Tinggi
                                </button>
                                <button
                                    onClick={() => updateSetting('contrast', 'dark')}
                                    className={`p-2 rounded-lg text-sm font-medium transition-all ${settings.contrast === 'dark'
                                            ? 'bg-[#0D9488] text-white'
                                            : 'bg-white border hover:bg-gray-50'
                                        }`}
                                >
                                    Mode Gelap
                                </button>
                            </div>
                        </div>

                        {/* Other Settings */}
                        <div className="space-y-2">
                            {/* Highlight Links */}
                            <button
                                onClick={() => toggleSetting('highlightLinks')}
                                className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${settings.highlightLinks
                                        ? 'bg-[#0D9488]/10 border-[#0D9488] border'
                                        : 'bg-gray-50 border border-transparent hover:bg-gray-100'
                                    }`}
                            >
                                <div className="flex items-center">
                                    <FaLink className={`mr-3 ${settings.highlightLinks ? 'text-[#0D9488]' : 'text-gray-500'}`} />
                                    <span className="text-sm font-medium text-gray-700">Sorot Tautan</span>
                                </div>
                                <div className={`w-10 h-6 rounded-full transition-all ${settings.highlightLinks ? 'bg-[#0D9488]' : 'bg-gray-300'
                                    }`}>
                                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-all m-0.5 ${settings.highlightLinks ? 'translate-x-4' : ''
                                        }`} />
                                </div>
                            </button>

                            {/* Large Cursor */}
                            <button
                                onClick={() => toggleSetting('largeCursor')}
                                className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${settings.largeCursor
                                        ? 'bg-[#0D9488]/10 border-[#0D9488] border'
                                        : 'bg-gray-50 border border-transparent hover:bg-gray-100'
                                    }`}
                            >
                                <div className="flex items-center">
                                    <FaMousePointer className={`mr-3 ${settings.largeCursor ? 'text-[#0D9488]' : 'text-gray-500'}`} />
                                    <span className="text-sm font-medium text-gray-700">Kursor Besar</span>
                                </div>
                                <div className={`w-10 h-6 rounded-full transition-all ${settings.largeCursor ? 'bg-[#0D9488]' : 'bg-gray-300'
                                    }`}>
                                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-all m-0.5 ${settings.largeCursor ? 'translate-x-4' : ''
                                        }`} />
                                </div>
                            </button>

                            {/* Hide Images */}
                            <button
                                onClick={() => toggleSetting('hideImages')}
                                className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${settings.hideImages
                                        ? 'bg-[#0D9488]/10 border-[#0D9488] border'
                                        : 'bg-gray-50 border border-transparent hover:bg-gray-100'
                                    }`}
                            >
                                <div className="flex items-center">
                                    <FaImage className={`mr-3 ${settings.hideImages ? 'text-[#0D9488]' : 'text-gray-500'}`} />
                                    <span className="text-sm font-medium text-gray-700">Sembunyikan Gambar</span>
                                </div>
                                <div className={`w-10 h-6 rounded-full transition-all ${settings.hideImages ? 'bg-[#0D9488]' : 'bg-gray-300'
                                    }`}>
                                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-all m-0.5 ${settings.hideImages ? 'translate-x-4' : ''
                                        }`} />
                                </div>
                            </button>

                            {/* Readable Font */}
                            <button
                                onClick={() => toggleSetting('readableFont')}
                                className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${settings.readableFont
                                        ? 'bg-[#0D9488]/10 border-[#0D9488] border'
                                        : 'bg-gray-50 border border-transparent hover:bg-gray-100'
                                    }`}
                            >
                                <div className="flex items-center">
                                    <FaFont className={`mr-3 ${settings.readableFont ? 'text-[#0D9488]' : 'text-gray-500'}`} />
                                    <span className="text-sm font-medium text-gray-700">Font Terbaca</span>
                                </div>
                                <div className={`w-10 h-6 rounded-full transition-all ${settings.readableFont ? 'bg-[#0D9488]' : 'bg-gray-300'
                                    }`}>
                                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-all m-0.5 ${settings.readableFont ? 'translate-x-4' : ''
                                        }`} />
                                </div>
                            </button>

                            {/* Letter Spacing */}
                            <button
                                onClick={() => updateSetting('letterSpacing', settings.letterSpacing === 'wide' ? 'normal' : 'wide')}
                                className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${settings.letterSpacing === 'wide'
                                        ? 'bg-[#0D9488]/10 border-[#0D9488] border'
                                        : 'bg-gray-50 border border-transparent hover:bg-gray-100'
                                    }`}
                            >
                                <div className="flex items-center">
                                    <FaTextHeight className={`mr-3 ${settings.letterSpacing === 'wide' ? 'text-[#0D9488]' : 'text-gray-500'}`} />
                                    <span className="text-sm font-medium text-gray-700">Jarak Huruf Lebar</span>
                                </div>
                                <div className={`w-10 h-6 rounded-full transition-all ${settings.letterSpacing === 'wide' ? 'bg-[#0D9488]' : 'bg-gray-300'
                                    }`}>
                                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-all m-0.5 ${settings.letterSpacing === 'wide' ? 'translate-x-4' : ''
                                        }`} />
                                </div>
                            </button>

                            {/* Line Height */}
                            <button
                                onClick={() => updateSetting('lineHeight', settings.lineHeight === 'tall' ? 'normal' : 'tall')}
                                className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${settings.lineHeight === 'tall'
                                        ? 'bg-[#0D9488]/10 border-[#0D9488] border'
                                        : 'bg-gray-50 border border-transparent hover:bg-gray-100'
                                    }`}
                            >
                                <div className="flex items-center">
                                    <FaEye className={`mr-3 ${settings.lineHeight === 'tall' ? 'text-[#0D9488]' : 'text-gray-500'}`} />
                                    <span className="text-sm font-medium text-gray-700">Jarak Baris Tinggi</span>
                                </div>
                                <div className={`w-10 h-6 rounded-full transition-all ${settings.lineHeight === 'tall' ? 'bg-[#0D9488]' : 'bg-gray-300'
                                    }`}>
                                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-all m-0.5 ${settings.lineHeight === 'tall' ? 'translate-x-4' : ''
                                        }`} />
                                </div>
                            </button>
                        </div>

                        {/* Keyboard Navigation Info */}
                        <div className="bg-blue-50 rounded-lg p-3">
                            <div className="flex items-center text-sm text-blue-800 mb-2">
                                <FaKeyboard className="mr-2" />
                                <span className="font-medium">Navigasi Keyboard</span>
                            </div>
                            <ul className="text-xs text-blue-700 space-y-1">
                                <li>• <kbd className="bg-white px-1 rounded">Tab</kbd> - Navigasi menu</li>
                                <li>• <kbd className="bg-white px-1 rounded">Enter</kbd> - Pilih/klik</li>
                                <li>• <kbd className="bg-white px-1 rounded">Esc</kbd> - Tutup panel</li>
                                <li>• <kbd className="bg-white px-1 rounded">Alt + ←/→</kbd> - Navigasi halaman</li>
                            </ul>
                        </div>

                        {/* Reset Button */}
                        <button
                            onClick={resetSettings}
                            className="w-full flex items-center justify-center p-3 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors font-medium"
                        >
                            <FaUndo className="mr-2" />
                            Kembalikan ke Pengaturan Awal
                        </button>
                    </div>

                    {/* Footer */}
                    <div className="bg-gray-50 p-3 text-center border-t">
                        <p className="text-xs text-gray-500 flex items-center justify-center">
                            <FaHandPaper className="mr-1" />
                            Mendukung aksesibilitas untuk semua
                        </p>
                    </div>
                </div>
            )}

            {/* Overlay */}
            {isOpen && (
                <div
                    className="fixed inset-0 z-[9997] bg-black/20"
                    onClick={() => setIsOpen(false)}
                />
            )}
        </>
    );
};

export default AccessibilityMenu;